import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories, reviews } from '@/mocks/comics';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);
  const category = categories.find((c) => c.slug === comic?.category);
  const relatedComics = comics
    .filter((c) => c.category === comic?.category && c.id !== comic?.id)
    .slice(0, 6);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-foreground-500 mb-4">请检查漫画地址是否正确</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const chapters = Array.from({ length: Math.min(comic.latestChapter, 20) }, (_, i) => ({
    number: comic.latestChapter - i,
    title: `第 ${comic.latestChapter - i} 话`,
    date: new Date(2026, 6, 1 - i).toISOString().split('T')[0],
  })).filter((ch) => ch.number > 0);

  const canonicalUrl = `http://vktest.com/article/${comic.slug}`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${comic.title} - 韩漫漫画网`,
    "description": comic.description,
    "author": {
      "@type": "Person",
      "name": comic.author
    },
    "datePublished": "2026-01-01",
    "dateModified": "2026-07-03",
    "url": canonicalUrl,
    "publisher": {
      "@type": "Organization",
      "name": "韩漫漫画网",
      "url": "http://vktest.com/"
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://vktest.com/" },
        { "@type": "ListItem", "position": 2, "name": `${category?.name || ''}漫画`, "item": `http://vktest.com/category/${comic.category}` },
        { "@type": "ListItem", "position": 3, "name": comic.title, "item": canonicalUrl }
      ]
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <section className="bg-background-50">
          <div className="px-4 md:px-6 py-8 md:py-10">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: category?.name ? `${category.name}漫画` : '漫画', path: `/category/${comic.category}` },
                { name: comic.title },
              ]}
            />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <div className="rounded-xl overflow-hidden bg-background-200 aspect-[3/4]">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 韩漫漫画网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="lg:col-span-8 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-foreground-100 text-foreground-700'
                  }`}>
                    {comic.status}
                  </span>
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {category.name}
                    </Link>
                  )}
                </div>

                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-2">
                  {comic.title}
                </h1>

                <p className="text-foreground-500 text-sm mb-4">作者：{comic.author}</p>

                <div className="flex flex-wrap gap-4 mb-6">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-primary-500"></i>
                    <span className="text-foreground-900 font-bold">{comic.rating}</span>
                    <span className="text-foreground-400 text-sm">评分</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-eye-line text-foreground-500"></i>
                    <span className="text-foreground-900 font-medium">{comic.views}</span>
                    <span className="text-foreground-400 text-sm">阅读</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-file-list-3-line text-foreground-500"></i>
                    <span className="text-foreground-900 font-medium">{comic.totalChapters}</span>
                    <span className="text-foreground-400 text-sm">话</span>
                  </div>
                </div>

                <p className="text-foreground-600 text-sm leading-relaxed mb-6">{comic.description}</p>

                <div className="flex flex-wrap gap-2 mb-8">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-background-200 text-foreground-600 text-xs whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={`/article/${comic.slug}`}
                    className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    开始阅读 <i className="ri-arrow-right-line"></i>
                  </Link>
                  <button className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-background-200 text-foreground-700 text-sm font-medium hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line"></i> 加入书架
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-10 md:py-14">
          <div className="px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
              章节列表
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {chapters.map((ch) => (
                <Link
                  key={ch.number}
                  to={`/article/${comic.slug}`}
                  className="flex items-center justify-between p-3.5 rounded-lg bg-background-50 hover:bg-background-200 transition-colors cursor-pointer group"
                >
                  <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors">
                    {ch.title}
                  </span>
                  <span className="text-xs text-foreground-400">{ch.date}</span>
                </Link>
              ))}
            </div>

            {comic.latestChapter > 20 && (
              <div className="text-center mt-6">
                <button className="px-6 py-2.5 rounded-full bg-background-200 text-foreground-600 text-sm font-medium hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap">
                  查看更多章节（共 {comic.totalChapters} 话）
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="bg-foreground-900 py-10 md:py-14">
          <div className="px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-1">
                  读者评价
                </h2>
                <p className="text-foreground-400 text-sm">看看其他读者怎么说</p>
              </div>
              <button className="px-5 py-2 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap">
                写评论
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {reviews.map((review) => (
                <div key={review.id} className="rounded-xl bg-foreground-800 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img src={review.avatar} alt={review.userName} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div>
                      <p className="text-background-50 text-sm font-medium">{review.userName}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i key={i} className={`text-xs ${i < review.rating ? 'ri-star-fill text-primary-400' : 'ri-star-line text-foreground-600'}`}></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-foreground-300 text-sm leading-relaxed">{review.content}</p>
                  <p className="text-foreground-500 text-xs mt-3">{review.date}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="bg-background-50 py-10 md:py-14">
            <div className="px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                同类型推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                {relatedComics.map((c) => (
                  <ComicCard
                    key={c.id}
                    id={c.id}
                    slug={c.slug}
                    title={c.title}
                    coverImage={c.coverImage}
                    rating={c.rating}
                    latestChapter={c.latestChapter}
                    status={c.status}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />

      <Footer />
    </>
  );
}